import { PocIcon, PocInput, PocList } from 'commons/components/components-wrapper';
import { MAX_OPTIONS_WITHOUT_SEARCH } from 'core/constants/constants';
import { getI18nCodeLanguage, useTranslation } from 'core/i18n/i18n';
import { useEffect, useState } from 'react';
import { DropdownButtonWithMenu } from '../dropdown-button-with-menu/dropdown-button-with-menu.component';
import { List, ListItemData } from '../list-item';
import { StyledLanguagePicker } from './language-picker.style';

interface LanguagePickerProps {
    languageSelected: string;
    languagesOptions: { value: string; text: string }[];
    onChangeLanguage: (languageSelected: string) => void;
}

export const LanguagePicker: React.FC<LanguagePickerProps> = (props) => {
    const { languageSelected, languagesOptions, onChangeLanguage } = props;
    const { i18n, t } = useTranslation();

    const [filter, setFilter] = useState<string>('');

    useEffect(() => {
        const updateLangAttribute = (lang: string) => {
            const codeLang: string = getI18nCodeLanguage(lang);
            document.documentElement.setAttribute('lang', codeLang);
            globalThis.Optanon?.changeLanguage(codeLang);
        };

        updateLangAttribute(i18n.language);

        i18n.on('languageChanged', updateLangAttribute);

        return () => {
            i18n.off('languageChanged', updateLangAttribute);
        };
    }, [i18n]);

    const getFilteredOptions = () =>
        languagesOptions.filter((option) => option.text.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));

    const selectedLanguageText =
        languagesOptions.find((option) => option.value === languageSelected)?.text ??
        languageSelected.toLocaleUpperCase();

    const getLanguageItems = (): ListItemData[] =>
        getFilteredOptions().map((language) => ({
            id: language.value,
            value: language.value,
            label: language.text,
            suffixSlot:
                language.value === languageSelected ? (
                    <PocIcon data-testid="language-selected-icon" name="confirm" />
                ) : undefined,
            className: language.value === languageSelected ? 'language-selected' : '',
            onClick: () => onChangeLanguage(language.value),
        }));

    return (
        <StyledLanguagePicker>
            <DropdownButtonWithMenu
                id="language-selector"
                variant="tertiary"
                buttonProps={{ 'data-testid': 'language-picker-button' }}
                buttonContent={() => selectedLanguageText}
                menuContent={
                    <>
                        {languagesOptions.length > MAX_OPTIONS_WITHOUT_SEARCH && (
                            <>
                                <PocInput
                                    data-testid="language-search"
                                    value={filter}
                                    placeholder={t('commons.components.searchable-dropdown.search.placeholder') ?? ''}
                                    onValueChange={(ev) => setFilter(ev.detail)}
                                    variant="outlined"
                                    size="small"
                                >
                                    <PocIcon slot="prefix" name="search" family="outlined" />
                                </PocInput>
                                <div style={{ margin: '1rem' }} />
                            </>
                        )}
                        <List items={getLanguageItems()} wrapper={PocList} />
                    </>
                }
            />
        </StyledLanguagePicker>
    );
};
